import { message, Progress, ProgressProps, Tooltip } from "antd";
import clsx from "clsx";
import React from "react";
import { useDropzone } from "react-dropzone";
import { MdOutlineRefresh, MdUploadFile } from "react-icons/md";
import { formatFileSize } from "../utils";

export enum DropzoneFileStatus {
  Idle = "idle",
  Uploading = "uploading",
  Failure = "failure",
  Success = "success",
}

export type DropzoneFile = {
  id: string;
  status: DropzoneFileStatus;
  progress: number;
  binary: File;
  size: number;
};

const DropzoneUploadedFile = (props: {
  file: DropzoneFile;
  removable?: boolean;
  showProgress?: boolean;
  onRemove?: () => void;
  onRetry?: () => void;
}) => {
  const progressStatus = React.useMemo<ProgressProps["status"]>(() => {
    switch (props.file.status) {
      case DropzoneFileStatus.Success:
        return "success";
      case DropzoneFileStatus.Failure:
        return "exception";
      default:
        return "active";
    }
  }, [props.file]);

  return (
    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex justify-between items-center">
        <div className="flex gap-3 items-center">
          <MdUploadFile className="w-4 h-4 text-gray-600" />
          <div>
            <p className="max-w-xs text-sm font-medium text-gray-800 truncate">
              {props.file.binary.name}
            </p>
            <p className="text-xs text-gray-500">
              {formatFileSize(props.file.size ?? props.file.binary.size)}
            </p>
          </div>
          {props.onRetry && props.file.status === DropzoneFileStatus.Failure && (
            <Tooltip title="Retry">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  props.onRetry?.();
                }}
              >
                <MdOutlineRefresh />
              </button>
            </Tooltip>
          )}
        </div>
        <button
          hidden={!props.removable}
          onClick={(e) => {
            e.stopPropagation();
            props.onRemove?.();
          }}
          className="text-lg font-bold text-gray-400 transition-colors hover:text-red-500"
          title="Remove file"
        >
          ×
        </button>
      </div>
      {props.showProgress && (
        <Progress percent={props.file.progress} status={progressStatus} />
      )}
    </div>
  );
};

export type DropzoneProps = {
  files: DropzoneFile[];
  maxFiles?: number;
  maxSizePerFile?: number;
  allowedExtensions?: string[];
  disabled?: boolean;
  onChange?: (files: DropzoneFile[]) => void;
  isUploading?: boolean;
  readonly?: boolean;
  retryable?: boolean;
  onRetry?: (file: DropzoneFile) => void;
};

export const Dropzone = (props: DropzoneProps) => {
  const {
    files,
    maxFiles = 1,
    maxSizePerFile = 1024 * 1024 * 10,
    allowedExtensions = ["*"],
    disabled = false,
    isUploading = false,
    readonly = false,
    retryable = false,
  } = props;

  const limit = React.useMemo(() => {
    if (!maxFiles) return undefined;
    return maxFiles - files.length;
  }, [maxFiles, files]);

  const dropzone = useDropzone({
    multiple: limit ? limit > 1 : undefined,
    maxFiles: limit,
    maxSize: maxSizePerFile,
    onDrop: (acceptedFiles, rejectedFiles) => {
      const newFiles = acceptedFiles.map<DropzoneFile>((file) => ({
        id: crypto.randomUUID(),
        status: DropzoneFileStatus.Idle,
        progress: 0,
        binary: file,
        size: file.size,
      }));

      props.onChange?.([...files, ...newFiles]);

      if (rejectedFiles.length) {
        message.warn(
          "Some of the uploaded files did not meet the acceptable file requirements and were ignored, please try again.",
        );
      }
    },
    disabled,
  });

  const handleRemove = (fileToRemove: DropzoneFile) => {
    props.onChange?.(files.filter((file) => file.id !== fileToRemove.id));
  };

  if (files.length) {
    return (
      <section>
        <p className="mb-2 text-sm font-semibold text-gray-700">
          Selected Files ({files.length})
        </p>
        <div className="overflow-y-auto space-y-2 max-h-80">
          {files.map((file) => (
            <DropzoneUploadedFile
              key={file.id}
              file={file}
              onRemove={() => handleRemove(file)}
              showProgress={isUploading || readonly}
              removable={!readonly}
              onRetry={retryable ? () => props.onRetry?.(file) : undefined}
            />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section
      className={clsx({
        "opacity-40 cursor-not-allowed pointer-events-none": props.disabled,
      })}
    >
      <div
        {...dropzone.getRootProps()}
        className="p-8 text-center rounded-lg border-2 border-gray-300 border-dashed transition-colors cursor-pointer hover:border-gray-400"
      >
        <div className="space-y-2">
          <p className="text-gray-600">
            Drop files here or{" "}
            <span className="font-semibold text-gray-800 underline cursor-pointer decoration-gray-300 decoration-2">
              choose a file
            </span>
          </p>
          <input {...dropzone.getInputProps()} />
          <label htmlFor="file-upload" className="cursor-pointer">
            <span className="sr-only">Choose files</span>
          </label>
        </div>
      </div>

      <div className="flex justify-between mb-4 text-sm text-gray-500">
        <small>
          Max files: {maxFiles}. Up to {formatFileSize(maxSizePerFile)}
        </small>
        <small>File types: {allowedExtensions.join(", ")}</small>
      </div>
    </section>
  );
};
